import React, { ReactNode } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { ETextDecoration } from '@sharedComponents/Buttons';
import { CheckoutPrice } from '@sharedComponents/Prices';
import { DEFAULT_CART_FONT_SIZE_TEXT_IN_REM } from '@styles/constants';
import { Normal } from '@styles/generalStyles';
import {
  capitalizeFirstChunk,
  removeColonFromEndOfChunk,
  removeColonFromLastChunk,
  removeDuplicateSpacesInString,
} from '@utils/text';
import { instanceOfString } from '@utils/typeGuards';

import { messages } from '../messages';
import { IBenefitItem } from '../model';
import { StyledTitleLink } from '../styled';

type TUseBenefitFormattedContent = (params: IBenefitItem) => {
  getBenefitTitle: () => string | React.ReactNode;
  getBenefitBody: () => string | React.ReactNode;
};

export const useBenefitFormattedContent: TUseBenefitFormattedContent = ({
  activationThreshold,
  freeDeliveryLimit,
  isRegistered,
  isSubscribed,
  onClick,
  price,
  date,
  isActive,
}) => {
  const { formatDate, formatMessage } = useIntl();

  const getBenefitTitle = () => {
    if (!isActive) {
      return formatMessage(messages.benefitFreeShippingInactiveTitle);
    }

    if (date) {
      const overPriceSection = freeDeliveryLimit
        ? formatMessage(messages.benefitOverPriceTitle, {
            price: (
              <CheckoutPrice
                firstCurrency={{
                  priceSize: DEFAULT_CART_FONT_SIZE_TEXT_IN_REM,
                  currencySize: DEFAULT_CART_FONT_SIZE_TEXT_IN_REM,
                  bold: true,
                }}
                secondCurrency={{
                  priceSize: DEFAULT_CART_FONT_SIZE_TEXT_IN_REM,
                  currencySize: DEFAULT_CART_FONT_SIZE_TEXT_IN_REM,
                  bold: true,
                }}
                separator={{
                  separatorSize: DEFAULT_CART_FONT_SIZE_TEXT_IN_REM,
                  bold: true,
                }}
                key={'title-price'}
                price={freeDeliveryLimit}
              />
            ),
          })
        : null;

      return (
        <FormattedMessage
          key={'conditionSection'}
          {...messages.benefitFreeShippingActiveTitle}
          values={{
            overPriceSection: overPriceSection,
            normal: (chunks) => <Normal key={'title-normal'}>{chunks}</Normal>,
            date: <Normal key={'title-date'}>{formatDate(date)}</Normal>,
          }}>
          {(chunks) => {
            return <>{removeDuplicateSpacesInString(chunks)}</>;
          }}
        </FormattedMessage>
      );
    }

    return formatMessage(messages.benefitFreeShippingInactiveTitle);
  };

  /**
   * The first case (isActive && activationThreshold) of the method is not needed yet.
   * TODO:
   * In the future the activationThreshold is going to become a nullable field and the case
   * should apply only if the limit has value 0 or higher and should be skipped for NULL and undefined
   */
  const getBenefitBody = () => {
    if (isActive) {
      return (
        <StyledTitleLink
          key={'link'}
          onClickAction={onClick}
          hasHover={true}
          disabled={false}
          textDecoration={ETextDecoration.UNDERLINE}>
          {formatMessage(messages.benefitFreeShippingMoreInformation)}
        </StyledTitleLink>
      );
    }

    const buyForMoreCondition = activationThreshold <= price;

    const isRegisteredSection = isRegistered ? '' : formatMessage(messages.benefitFreeShippingRegisterDescription);
    const isSubscribedSection = isSubscribed ? '' : formatMessage(messages.benefitFreeShippingSubscribeDescription);
    const buyForMoreSection = buyForMoreCondition
      ? ''
      : formatMessage(messages.benefitFreeShippingBuyForMoreDescription, {
          price: <CheckoutPrice key={'price'} price={activationThreshold - price} />,
        });

    if (!buyForMoreCondition || !isRegistered || !isSubscribed) {
      const conditionSection = (
        <FormattedMessage
          key={'conditionSection'}
          {...messages.benefitFreeShippingConditionDescription}
          values={{
            buyForMoreSection: buyForMoreSection,
            registerSection: isRegisteredSection,
            subScribeSection: isSubscribedSection,
          }}>
          {(chunks) => {
            return <>{processConditionSectionChunks(chunks)}</>;
          }}
        </FormattedMessage>
      );

      return formatMessage(messages.benefitFreeShippingConditionsToBeMetDescription, {
        conditionSection: conditionSection,
      });
    }

    return formatMessage(messages.benefitFreeShippingCompletePurchase);
  };

  return { getBenefitTitle, getBenefitBody };
};

const processConditionSectionChunks = (chunks: ReactNode[]) => {
  // if last chunks is empty, remove colon from second to last chunk
  const lastChunkValue = chunks[chunks.length - 1];
  const secondToLastChunkValue = chunks[chunks.length - 2];
  if (instanceOfString(lastChunkValue) && !lastChunkValue.trim() && instanceOfString(secondToLastChunkValue)) {
    chunks[chunks.length - 2] = removeColonFromEndOfChunk(secondToLastChunkValue);
  }

  return removeColonFromLastChunk(removeDuplicateSpacesInString(capitalizeFirstChunk(chunks)));
};
